"use client";

import dynamic from "next/dynamic";
import React, { createContext, useContext, useState, useCallback } from "react";
import type { Toast, ToastActions, ToastType } from "@/components/ui/toast";

interface ToastContextType {
  showToast: (
    description: string,
    type?: ToastType,
    title?: string,
    duration?: number,
    action?: ToastActions,
  ) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const DeferredToastContainer = dynamic(
  () => import("@/components/ui/toast").then((module) => module.ToastContainer),
  { ssr: false },
);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export function useOptionalToast(): ToastContextType | undefined {
  return useContext(ToastContext);
}

let toastIdCounter = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((
    description: string,
    type: ToastType = "info",
    title?: string,
    duration?: number,
    action?: ToastActions,
  ) => {
    const id = `toast-${++toastIdCounter}`;
    const newToast: Toast = {
      id,
      description,
      type,
      title,
      duration,
      action,
    };

    setToasts((prev) => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      {toasts.length > 0 ? (
        <DeferredToastContainer toasts={toasts} onDismiss={dismissToast} />
      ) : null}
    </ToastContext.Provider>
  );
}
